import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Tooltip, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';

import { ROUTES } from '@/routes';
import { usePageBackLink } from '@/hooks';
import { ResultsExportModal } from '@/components/ResultsExportModal';
import { AdminService, type AdminHtsSubmissionDetail } from '@/services/adminService';
import { formatDate, formatDateTime, formatGuestLabel, formatUserName } from '@/utils/formatters';
import { HtsSection, type HtsReleaseStatus, type HtsSectionHandle } from './AdminHtsSection';
import styles from './AdminSessionResults.module.scss';

/**
 * Standalone HTS submission view — reachable directly from the admin results
 * list (AdminAssessments.tsx's "HTS Submissions" tab). Every HTS submission
 * is reviewed here, on its own: HTS and the competency interview are started
 * and retaken independently, so a submission may have no Assessment at all,
 * or several unrelated ones.
 */
export const AdminHtsSubmissionResults: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [submission, setSubmission] = useState<AdminHtsSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [releaseStatus, setReleaseStatus] = useState<HtsReleaseStatus | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [followUpDraftCount, setFollowUpDraftCount] = useState(0);
  const htsSectionRef = useRef<HtsSectionHandle>(null);

  const fetchSubmission = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const data = await new AdminService(token).getHtsSubmission(submissionId);
      setSubmission(data);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load HTS submission' });
    } finally {
      setLoading(false);
    }
  }, [submissionId, getToken, notification]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  usePageBackLink(() => navigate(ROUTES.ADMIN_ASSESSMENTS), 'Back to Results');

  if (loading) {
    return (
      <div className={styles.page}>
        <Typography.Text type="secondary">Loading...</Typography.Text>
      </div>
    );
  }

  if (!submission) return null;

  // "No follow-ups needed" is a round with no follow-up on any answer.
  const followUps = submission.adminFollowUps;
  const hasFollowUpRound = submission.answers.some((a) => a.adminFollowUp !== null);
  // The server still refuses a send once login has ended; this only drives the disabled state and copy.
  const loginEndsAt = submission.guestLoginEndsAt;
  const loginClosed =
    loginEndsAt && new Date(loginEndsAt).getTime() <= Date.now() ? `access ended ${formatDate(loginEndsAt)}` : null;
  const loginOpenUntil = loginEndsAt && !loginClosed ? loginEndsAt : null;

  const userName = submission.accessCodeTags
    ? formatGuestLabel(submission.accessCodeTags)
    : formatUserName(submission.user.firstName, submission.user.lastName) || submission.user.email;

  return (
    <div className={styles.page}>
      <div className={styles.meta}>
        <div className={styles.titleRow}>
          <Typography.Title level={2} className={styles.title}>
            {userName}
          </Typography.Title>
          <div className={styles.releaseBar}>
            {submission.isGuest && !followUps && (
              <Tooltip title={loginClosed ? `This guest can no longer log in (${loginClosed})` : undefined}>
                <Button disabled={loginClosed !== null} onClick={() => htsSectionRef.current?.openSendFollowUps()}>
                  {followUpDraftCount > 0 ? `Send follow-up questions (${followUpDraftCount})` : 'No follow-ups needed'}
                </Button>
              </Tooltip>
            )}
            <Button onClick={() => setExportOpen(true)}>Export PDF</Button>
            {releaseStatus && releaseStatus.stagedCount > 0 && (
              <Typography.Text className={styles.stagedCount}>
                {releaseStatus.answerOverrideCount + releaseStatus.flagOverrideCount} override
                {releaseStatus.answerOverrideCount + releaseStatus.flagOverrideCount === 1 ? '' : 's'},{' '}
                {releaseStatus.dimensionOverrideCount} dimension score{releaseStatus.dimensionOverrideCount === 1 ? '' : 's'},{' '}
                {releaseStatus.noteOnlyCount} note{releaseStatus.noteOnlyCount === 1 ? '' : 's'} staged
              </Typography.Text>
            )}
            {releaseStatus && (
              <Button
                type="primary"
                disabled={!releaseStatus.canRelease}
                onClick={() => htsSectionRef.current?.openRelease()}
              >
                Review readiness profile
              </Button>
            )}
          </div>
        </div>
        <Typography.Text type="secondary" className={styles.pageSub}>

          {submission.interviewTemplateName}
          {!submission.accessCodeTags && <> &middot; {submission.user.email}</>}
          {submission.submittedAt && (
            <> &middot; Submitted {new Date(submission.submittedAt).toLocaleDateString()}</>
          )}
        </Typography.Text>
        {submission.isGuest && !followUps && loginOpenUntil && (
          <Typography.Text className={styles.followUpStatusLine}>
            Guest can log in until {formatDateTime(loginOpenUntil)}. Send any follow-up questions
            early enough for them to answer before then.
          </Typography.Text>
        )}
        {followUps && (
          <Typography.Text className={styles.followUpStatusLine}>
            {hasFollowUpRound ? 'Follow-up questions sent' : 'No follow-up questions needed: confirmed'}{' '}
            {formatDateTime(followUps.sentAt)} by {followUps.sentByName ?? 'an admin'}
            {hasFollowUpRound &&
              (followUps.answeredAt
                ? ` · answered ${formatDateTime(followUps.answeredAt)}`
                : loginClosed
                  ? ` · not answered: guest can no longer log in (${loginClosed})`
                  : ` · awaiting answers${
                    loginOpenUntil
                      ? ` (guest can log in until ${formatDateTime(loginOpenUntil)})`
                      : ''
                  }`)}
          </Typography.Text>
        )}
      </div>

      <HtsSection
        ref={htsSectionRef}
        hts={submission}
        submissionId={submission.id}
        onSaved={setSubmission}
        onReleaseStatusChange={setReleaseStatus}
        onFollowUpDraftCountChange={setFollowUpDraftCount}
      />

      <ResultsExportModal
        open={exportOpen}
        selection={{ assessmentIds: [], htsSubmissionIds: [submission.id] }}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
};

export default AdminHtsSubmissionResults;
